import { IMetadata } from 'app/services/rtc/sync/ActionStream';
import { SyncState } from 'app/models/rtc/sync/constants';

// What follows is a nearly pure SOCT4 implementation,
// using the specified actionHandler
//
// Unfortunately hacked together at maximum speed.
// two significant exceptions:
//
//   we execute in the global order instead of transposing new ops
//     - this facilitates non-OT LWW documents
//
//   we have a global ticket number and assign just before sending
//     - this facilitates batching of actions. When our ticket number arrives
//       we can send up every buffered local operation.

export enum ActionState {
  /**
   * Local pending actions that are not sent to remote yet
   */
  Pending   = 'pending',
  /**
   * Remote actions received with continguous `position`
   */
  Confirmed = 'confirmed',
  /**
   * Remote actions that have noncontinguous `position`
   *
   * In other words, if stream has any gaps in continuity of
   * actions, any remote actions after the gap will be considered
   * `ConfirmedOutOfOrder`. This can happen as a result of a race
   * condition, for example:
   * - sent local action is ACKd before we receive action preceding
   *   it in the stream
   * - RT sends us a new action while we're still receiving initial
   *   list of action with `subscribe`
   */
  ConfirmedOutOfOrder = 'confirmedOutOfOrder',
}

export enum Events {
  OPENED           = 'opened',
  REMOTE_UPDATED   = 'remote_updated',
  CHANGED          = 'changed',
  SYNCED           = 'synced',
  METADATA_CHANGED = 'metadata_changed',
  CLOSED           = 'closed',
}

export interface IRemoteUpdatedEvent<DocumentT, ActionT> {
  actions: ActionT[];
  state: DocumentT;
}

export interface IChangedEvent<DocumentT, ActionT> {
  actions: ActionT[];
  state: DocumentT;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IOpenedEvent {}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IClosedEvent {}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ISyncedEvent {
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IMetadataChangedEvent extends IMetadata {
}

export interface IEventTypeTable<DocumentT, ActionT> {
  [Events.OPENED]: IOpenedEvent;
  [Events.CLOSED]: IClosedEvent;
  [Events.REMOTE_UPDATED]: IRemoteUpdatedEvent<DocumentT, ActionT>;
  [Events.CHANGED]: IChangedEvent<DocumentT, ActionT>;
  [Events.SYNCED]: ISyncedEvent;
  [Events.METADATA_CHANGED]: IMetadataChangedEvent;
}

export type EventHandler<E> = (event: E) => void;

export interface IDocumentManager<DocumentT, ActionT> {
  // Metadata
  readonly metadata: Readonly<IMetadata>;

  // Current speculative state of the synced document.
  getLatestSnapshot(): DocumentT;

  // Start managing document. No events will be emitted before this is called.
  start(): void;

  // Stop managing document.
  shutdown(): void;

  readonly status: SyncState;

  addListener<E extends keyof IEventTypeTable<DocumentT, ActionT>>(
    eventName: E,
    handler: EventHandler<IEventTypeTable<DocumentT, ActionT>[E]>,
  ): void;

  removeListener<E extends keyof IEventTypeTable<DocumentT, ActionT>>(
    eventName: E,
    handler: EventHandler<IEventTypeTable<DocumentT, ActionT>[E]>,
  ): void;

  prependListener<E extends keyof IEventTypeTable<DocumentT, ActionT>>(
    eventName: E,
    handler: EventHandler<IEventTypeTable<DocumentT, ActionT>[E]>,
  ): void;
}

export class DummyDocumentManager<DocumentT, ActionT>
implements IDocumentManager<DocumentT, ActionT> {
  public readonly metadata: Readonly<IMetadata> = { blockedReasons: [] };
  public readonly status = SyncState.closed;

  public constructor(
    actionHandler: unknown,
    actionStream: unknown,
    public snapshot: DocumentT,
  ) {}

  public start() {}
  public shutdown() {}
  public getLatestSnapshot() {
    return this.snapshot;
  }
  public addListener() {}
  public removeListener() {}
  public prependListener() {}
}
